import { useState, type ReactNode } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material'

import { useGetPartnerAccessQuery, useRevokePartnerAccessMutation } from '@/apis/teamApi'
import { partnerName, partnerScopeLabel, revokeConsequences } from '@/apis/teamTypes'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'

const apiMessage = (err: unknown, fallback: string) =>
    (err as { data?: { error?: { message?: string } } })?.data?.error?.message ?? fallback

const dateOf = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : 'Never')

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }} sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
                {label}
            </Typography>
            <Box>{children}</Box>
        </Stack>
    )
}

/**
 * Settings > Access: the partner that set this organization up, what it can do here, and a
 * way to remove that access for good (DELETE /v2/orgs/current/partner-access). Owners and
 * admins of a claimed partner org only; the nav hides it for everyone else.
 */
export function AccessPage() {
    const dispatch = useAppDispatch()
    const { data, isLoading, error } = useGetPartnerAccessQuery()
    const [revoke, { isLoading: revoking, error: revokeError }] = useRevokePartnerAccessMutation()
    const [confirming, setConfirming] = useState(false)

    const header = (
        <>
            <Typography variant="h5" sx={{ mb: 1 }}>
                Access
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Who else can work with this organization's analytics.
            </Typography>
        </>
    )

    if (isLoading) {
        return <Box sx={{ p: 3, maxWidth: 800 }}>{header}</Box>
    }
    if (error || !data) {
        return (
            <Box sx={{ p: 3, maxWidth: 800 }}>
                {header}
                <Alert severity="error">{apiMessage(error, 'Could not load access')}</Alert>
            </Box>
        )
    }
    if (data.status === 'none') {
        return (
            <Box sx={{ p: 3, maxWidth: 800 }}>
                {header}
                <Alert severity="info" data-testid="no-partner">
                    No partner has access to this organization.
                </Alert>
            </Box>
        )
    }

    const name = partnerName(data)
    const active = data.status === 'active'
    const confirm = async () => {
        try {
            await revoke().unwrap()
            setConfirming(false)
            dispatch(
                showNotification({ message: `${name} no longer has access`, severity: 'success' }),
            )
        } catch {
            // shown in the dialog
        }
    }

    return (
        <Box sx={{ p: 3, maxWidth: 800 }}>
            {header}
            <Card>
                <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Typography variant="h6" data-testid="partner-name">
                            {name}
                        </Typography>
                        <Chip
                            size="small"
                            label={active ? 'Has access' : 'Access removed'}
                            color={active ? 'success' : 'default'}
                            data-testid="partner-access-status"
                        />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {name} set up this organization for you.
                        {active
                            ? ' It can still work with it as described below.'
                            : " It can't reach this organization any more. Your data stays here."}
                    </Typography>
                    <Row label="Access since">{dateOf(data.granted_at)}</Row>
                    {active ? (
                        <>
                            <Row label="Can">
                                {data.scopes.length === 0 ? (
                                    <Typography variant="body2">
                                        Nothing right now (it has no active keys)
                                    </Typography>
                                ) : (
                                    data.scopes.map(s => (
                                        <Typography key={s} variant="body2">
                                            {partnerScopeLabel(s)}
                                        </Typography>
                                    ))
                                )}
                            </Row>
                            <Row label="Last used">
                                <Typography variant="body2">{dateOf(data.last_used_at)}</Typography>
                            </Row>
                        </>
                    ) : (
                        <Row label="Removed">
                            <Typography variant="body2">
                                {dateOf(data.revoked_at)}
                                {data.revoked_by
                                    ? ` by ${data.revoked_by.email ?? data.revoked_by.name ?? data.revoked_by.id}`
                                    : ''}
                            </Typography>
                        </Row>
                    )}
                    {data.can_revoke && (
                        <Box sx={{ mt: 2 }}>
                            <Button
                                color="error"
                                variant="outlined"
                                onClick={() => setConfirming(true)}
                                data-testid="revoke-partner-access"
                            >
                                Remove {name}'s access
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Remove {name}'s access?</DialogTitle>
                <DialogContent>
                    {revokeError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {apiMessage(revokeError, 'Could not remove access')}
                        </Alert>
                    )}
                    <Box component="ul" sx={{ pl: 2, m: 0 }} data-testid="revoke-consequences">
                        {revokeConsequences(name).map(line => (
                            <Typography component="li" variant="body2" key={line} sx={{ mb: 1 }}>
                                {line}
                            </Typography>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(false)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={revoking}
                        onClick={() => {
                            confirm().catch(() => undefined)
                        }}
                        data-testid="confirm-revoke"
                    >
                        {revoking ? 'Removing…' : 'Remove access'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
